import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import SettingsDialog from "../components/dialogs/SettingsDialog";
import {
  getFromLocalStorage,
  putToLocalStorage,
} from "@/services/localStorageService";
import { TeamReference } from "@/models/pdfData";
import { setCookie } from "@/services/cookieService";
import { useLocation } from "react-router-dom";

const settingsVersion = 8;
export type ViewMode = "tablet" | "challenge";

export type HighlightForOptions = "gameCaptain" | "setter";

export interface AppSettings {
  settingsVersion: number;
  useFirstRefPerspective: boolean;
  highlightSetterAutomatically: boolean;
  highlightedSetter: { [key in TeamReference]: string };
  highlightedGameCaptain: { [key in TeamReference]: string };
  useHighlightFor: HighlightForOptions;
  showPlayerNames: boolean;
  usePlayerFirstNames: boolean;
  challengeTickerLocation: "left" | "right";
  challengeOverrideRemaining: boolean;
  attemptAlwaysOnScreen: boolean;
  useLocalStorage: boolean;
}

interface AppDataValues extends AppSettings {
  setUseFirstRefPerspective: (newValue: boolean) => void;
  setHighlightSetterAutomatically: (newValue: boolean) => void;
  setUseHighlightFor: (newValue: HighlightForOptions) => void;
  highlightPlayer: (
    team: TeamReference,
    playerUuid: string,
    highlightFor: HighlightForOptions,
  ) => void;
  highlightGameCaptain: (team: TeamReference, playerUuid: string) => void;
  setShowPlayerNames: (newValue: boolean) => void;
  setUsePlayerFirstNames: (newValue: boolean) => void;
  setChallengeTickerLocation: (
    newValue: AppSettings["challengeTickerLocation"],
  ) => void;
  setChallengeOverrideRemaining: (newValue: boolean) => void;
  setAttemptAlwaysOnScreen?: (newValue: boolean) => void;
  setUseLocalStorage?: (newValue: boolean) => void;
  currentView: ViewMode;
  openSettingsDialog: () => void;
}

const notInitializedYet = (): any => {
  console.error("AppDataContext has not been initialized yet!");
};

const defaultAppSettings: AppSettings = {
  settingsVersion,
  useFirstRefPerspective: false,
  highlightSetterAutomatically: true,
  highlightedSetter: { team1: "", team2: "" },
  highlightedGameCaptain: { team1: "", team2: "" },
  useHighlightFor: "gameCaptain",
  showPlayerNames: false,
  usePlayerFirstNames: false,
  challengeTickerLocation: "right",
  challengeOverrideRemaining: false,
  attemptAlwaysOnScreen: true,
  useLocalStorage: true,
};

const AppDataContext = createContext<AppDataValues>({
  ...defaultAppSettings,
  currentView: "tablet",
  highlightPlayer: notInitializedYet,
  highlightGameCaptain: notInitializedYet,
  setUseFirstRefPerspective: notInitializedYet,
  setHighlightSetterAutomatically: notInitializedYet,
  setUseHighlightFor: notInitializedYet,
  openSettingsDialog: notInitializedYet,
  setShowPlayerNames: notInitializedYet,
  setUsePlayerFirstNames: notInitializedYet,
  setChallengeTickerLocation: notInitializedYet,
  setChallengeOverrideRemaining: notInitializedYet,
  setAttemptAlwaysOnScreen: notInitializedYet,
  setUseLocalStorage: notInitializedYet,
});

export const useAppDataContext = (): AppDataValues =>
  useContext(AppDataContext);

const reducer: React.Reducer<
  AppSettings,
  | { type: "setShowPlayerNames"; value: boolean }
  | { type: "setUsePlayerFirstNames"; value: boolean }
  | { type: "storePartialAppSettings"; partialSettings: Partial<AppSettings> }
  | { type: "setUseFirstRefPerspective"; value: boolean }
  | { type: "setHighlightSetterAutomatically"; value: boolean }
  | {
      type: "setChallengeTickerLocation";
      value: AppSettings["challengeTickerLocation"];
    }
  | { type: "setChallengeOverrideRemaining"; value: boolean }
  | { type: "setUseHighlightFor"; value: HighlightForOptions }
  | {
      type: "storeHighlightSetterId" | "storeHighlightGameCaptainId";
      team: TeamReference;
      playerUuid: string;
    }
  | { type: "setAttemptAlwaysOnScreen"; value: boolean }
  | { type: "setUseLocalStorage"; value: boolean }
> = (state, action) => {
  switch (action.type) {
    case "storePartialAppSettings":
      return { ...state, ...action.partialSettings };
    case "setUseFirstRefPerspective":
      return { ...state, useFirstRefPerspective: action.value };
    case "setShowPlayerNames":
      return { ...state, showPlayerNames: action.value };
    case "setUsePlayerFirstNames":
      return { ...state, usePlayerFirstNames: action.value };
    case "setHighlightSetterAutomatically":
      return {
        ...state,
        highlightSetterAutomatically: action.value,
      };
    case "setUseHighlightFor":
      return { ...state, useHighlightFor: action.value };
    case "storeHighlightSetterId":
      return {
        ...state,
        highlightedSetter: {
          ...state.highlightedSetter,
          [action.team]:
            state.highlightedSetter[action.team] === action.playerUuid
              ? null
              : action.playerUuid,
        },
      };
    case "storeHighlightGameCaptainId":
      return {
        ...state,
        highlightedGameCaptain: {
          ...state.highlightedGameCaptain,
          [action.team]:
            state.highlightedGameCaptain[action.team] === action.playerUuid
              ? null
              : action.playerUuid,
        },
      };
    case "setChallengeTickerLocation":
      return { ...state, challengeTickerLocation: action.value };
    case "setChallengeOverrideRemaining":
      return { ...state, challengeOverrideRemaining: action.value };
    case "setAttemptAlwaysOnScreen":
      return { ...state, attemptAlwaysOnScreen: action.value };
    case "setUseLocalStorage":
      return { ...state, useLocalStorage: action.value };
  }
};

const getSettingsFromLocalStorageAndUpdate = () => {
  const settings = getFromLocalStorage("settings") || defaultAppSettings;
  if (
    !settings?.settingsVersion ||
    settings.settingsVersion < settingsVersion
  ) {
    return { ...defaultAppSettings, ...settings, settingsVersion };
  } else {
    return settings;
  }
};

const AppDataContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation();
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settings, dispatch] = useReducer(
    reducer,
    getSettingsFromLocalStorageAndUpdate(),
  );

  const [currentView, setCurrentView] = useState<ViewMode>("tablet");

  useEffect(() => {
    if (pathname.includes("/challenge")) {
      setCurrentView("challenge");
    } else {
      setCurrentView("tablet");
    }
  }, [pathname]);

  useEffect(() => {
    putToLocalStorage("settings", settings);
    setCookie("deviceCheck", true, { allSubdomains: true, expiresInDays: 14 });
  }, [settings]);

  const openSettingsDialog = () => setSettingsDialogOpen(true);

  const highlightPlayer: AppDataValues["highlightPlayer"] = (
    team,
    playerUuid,
    highlightFor,
  ) => {
    dispatch({
      type:
        highlightFor === "setter"
          ? "storeHighlightSetterId"
          : "storeHighlightGameCaptainId",
      team,
      playerUuid,
    });
  };

  const highlightGameCaptain: AppDataValues["highlightGameCaptain"] = (
    team,
    playerUuid,
  ) => {
    dispatch({
      type: "storeHighlightGameCaptainId",
      team,
      playerUuid,
    });
  };

  const setHighlightSetterAutomatically: AppDataValues["setHighlightSetterAutomatically"] =
    (newValue) =>
      dispatch({ type: "setHighlightSetterAutomatically", value: newValue });

  const setUseHighlightFor: AppDataValues["setUseHighlightFor"] = (newValue) =>
    dispatch({ type: "setUseHighlightFor", value: newValue });

  const setUseFirstRefPerspective: AppDataValues["setUseFirstRefPerspective"] =
    (newValue) =>
      dispatch({ type: "setUseFirstRefPerspective", value: newValue });

  const setUsePlayerFirstNames: AppDataValues["setUsePlayerFirstNames"] = (
    newValue,
  ) => dispatch({ type: "setUsePlayerFirstNames", value: newValue });

  const setShowPlayerNames: AppDataValues["setShowPlayerNames"] = (newValue) =>
    dispatch({ type: "setShowPlayerNames", value: newValue });

  const setChallengeTickerLocation: AppDataValues["setChallengeTickerLocation"] =
    (newValue) =>
      dispatch({ type: "setChallengeTickerLocation", value: newValue });

  const setChallengeOverrideRemaining: AppDataValues["setChallengeOverrideRemaining"] =
    (newValue) =>
      dispatch({ type: "setChallengeOverrideRemaining", value: newValue });

  const setAttemptAlwaysOnScreen: AppDataValues["setAttemptAlwaysOnScreen"] = (
    newValue,
  ) => dispatch({ type: "setAttemptAlwaysOnScreen", value: newValue });

  const setUseLocalStorage: AppDataValues["setUseLocalStorage"] = (newValue) =>
    dispatch({ type: "setUseLocalStorage", value: newValue });

  return (
    <AppDataContext.Provider
      value={{
        ...settings,
        currentView,
        highlightPlayer,
        highlightGameCaptain,
        setUseHighlightFor,
        setHighlightSetterAutomatically,
        openSettingsDialog,
        setUseFirstRefPerspective,
        setShowPlayerNames,
        setUsePlayerFirstNames,
        setChallengeTickerLocation,
        setChallengeOverrideRemaining,
        setAttemptAlwaysOnScreen,
        setUseLocalStorage,
      }}
    >
      <SettingsDialog
        open={settingsDialogOpen}
        onClose={(newSettings: Partial<AppSettings>) => {
          setSettingsDialogOpen(false);
          dispatch({
            type: "storePartialAppSettings",
            partialSettings: newSettings,
          });
        }}
      />
      {children}
    </AppDataContext.Provider>
  );
};

export default AppDataContextProvider;
